import { useState, useCallback } from 'react';
import { 
    Dialog, 
    Button, 
    CircularProgress,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

const BranchCreateModal = ({ open, onClose, onSave, isSaving = false }) => {

    const [formData, setFormData] = useState({
        Code: '',
        Name: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        StateCodeId: '',
        ContactNumber1: '',
        ContactNumber2: '',
        ContactNumber3: '',
        Email: '',
        VATNumber: '',
        PanNumber: '',
        ImporterCode: '',
        TradeLicenseNo: '',
        PaymentBankId: '',
        BillPrefix: '',
        // BToCPrefix: '',
        // IGSTPrefix: '',
        // IGSTCustomerPrefix: '',
        CashOpeningBalance: '',
        CashOpeningDate: '',
        StockOpeningBalance: '',
        StockOpeningDate: '',
    });

    const [validationErrors, setValidationErrors] = useState({});

    const validateForm = useCallback(() => {
        const errors = {};
        if (!formData.Code?.trim()) {
            errors.Code = 'Branch code is required';
        }
        if (!formData.Name?.trim()) {
            errors.Name = 'Branch name is required';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData.Code, formData.Name]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [validationErrors]);

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    const handleClose = useCallback(() => {
        setFormData({
            Code: '',
            Name: '',
            AddressLine1: '',
            AddressLine2: '',
            Location: '',
            PinCode: '',
            StateCodeId: '',
            ContactNumber1: '',
            ContactNumber2: '',
            ContactNumber3: '',
            Email: '',
            VATNumber: '',
            PanNumber: '',
            ImporterCode: '',
            TradeLicenseNo: '',
            PaymentBankId: '',
            BillPrefix: '',
            // BToCPrefix: '',
            // IGSTPrefix: '',
            // IGSTCustomerPrefix: '',
            CashOpeningBalance: '',
            CashOpeningDate: '',
            StockOpeningBalance: '',
            StockOpeningDate: '',
        });
        setValidationErrors({});
        onClose();
    }, [onClose]);

    const isFormValid = !!(formData.Code && formData.Name) && Object.keys(validationErrors).length === 0;

    // Styles
    const sectionHeaderStyle = {
        background: '#1e3a5f',
        color: 'white',
        padding: '8px 14px',
        fontSize: '12px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderRadius: '4px',
        marginTop: '16px',
    };

    const labelStyle = {
        display: 'block',
        fontSize: '11px',
        fontWeight: '700',
        color: '#1e293b',
        textTransform: 'uppercase',
        marginBottom: '4px',
    };

    const inputStyle = {
        width: '100%',
        padding: '8px 10px',
        fontSize: '13px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        outline: 'none',
        color: '#1e293b',
        fontFamily: 'inherit',
        background: 'white',
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{
                style: {
                    borderRadius: '8px',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Modal Header */}
            <div style={{
                background: '#1e3a5f',
                color: 'white',
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AddIcon style={{ fontSize: '18px' }} />
                    <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase' }}>
                        Create New Branch
                    </span>
                </div>
                <IconButton onClick={handleClose} size="small" style={{ color: 'white' }}>
                    <CloseIcon style={{ fontSize: '20px' }} />
                </IconButton>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '0 20px 20px', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Basic Information */}
                <div style={sectionHeaderStyle}>Basic Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Branch Code <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            name="Code"
                            value={formData.Code}
                            onChange={handleChange}
                            maxLength={5}
                            placeholder="e.g., BR001"
                            style={{ ...inputStyle, borderColor: validationErrors.Code ? '#ef4444' : '#d1d5db' }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = validationErrors.Code ? '#ef4444' : '#d1d5db'}
                        />
                        {validationErrors.Code && <span style={{ fontSize: '11px', color: '#ef4444' }}>{validationErrors.Code}</span>}
                    </div>
                    <div>
                        <label style={labelStyle}>Branch Name <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            name="Name"
                            value={formData.Name}
                            onChange={handleChange}
                            maxLength={150}
                            placeholder="Enter branch name"
                            style={{ ...inputStyle, borderColor: validationErrors.Name ? '#ef4444' : '#d1d5db' }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = validationErrors.Name ? '#ef4444' : '#d1d5db'}
                        />
                        {validationErrors.Name && <span style={{ fontSize: '11px', color: '#ef4444' }}>{validationErrors.Name}</span>}
                    </div>
                </div>

                {/* Address Details */}
                <div style={sectionHeaderStyle}>Address Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Address Line 1</label>
                        <input name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={200} placeholder="Street address" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Address Line 2</label>
                        <input name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={200} placeholder="Apartment, suite" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>City</label>
                        <input name="Location" value={formData.Location} onChange={handleChange} maxLength={50} placeholder="City name" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Pin Code</label>
                        <input name="PinCode" value={formData.PinCode} onChange={handleChange} maxLength={6} placeholder="123456" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>State Code ID</label>
                        <input name="StateCodeId" type="number" value={formData.StateCodeId} onChange={handleChange} placeholder="State ID" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>

                {/* Contact Details */}
                <div style={sectionHeaderStyle}>Contact Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Primary Contact</label>
                        <input name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} maxLength={15} placeholder="+91 XXXXX XXXXX" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Secondary Contact</label>
                        <input name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} maxLength={15} placeholder="+91 XXXXX XXXXX" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Alternate Contact</label>
                        <input name="ContactNumber3" value={formData.ContactNumber3} onChange={handleChange} maxLength={15} placeholder="+91 XXXXX XXXXX" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>Email</label>
                    <input name="Email" type="email" value={formData.Email} onChange={handleChange} maxLength={100} placeholder="branch@company.com" style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                </div>

                {/* Tax Details */}
                <div style={sectionHeaderStyle}>Tax Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>VAT Number</label>
                        <input name="VATNumber" value={formData.VATNumber} onChange={handleChange} maxLength={15} placeholder="22AAAAA0000A1Z5" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>PAN Number</label>
                        <input name="PanNumber" value={formData.PanNumber} onChange={handleChange} maxLength={10} placeholder="AAAAA0000A" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Importer Code / Customs No</label>
                        <input name="ImporterCode" value={formData.ImporterCode} onChange={handleChange} maxLength={50} placeholder="Importer Code" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Trade License No</label>
                        <input name="TradeLicenseNo" value={formData.TradeLicenseNo} onChange={handleChange} maxLength={50} placeholder="License No" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Payment Bank ID</label>
                        <input name="PaymentBankId" type="number" value={formData.PaymentBankId} onChange={handleChange} placeholder="Bank ID" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Bill Prefix</label>
                        <input name="BillPrefix" value={formData.BillPrefix} onChange={handleChange} maxLength={5} placeholder="B2B" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>

              
                {/* Opening Balances */}
                <div style={sectionHeaderStyle}>Opening Balances</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Cash Opening Balance</label>
                        <input name="CashOpeningBalance" type="number" value={formData.CashOpeningBalance} onChange={handleChange} placeholder="0.00" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Cash Opening Date</label>
                        <input name="CashOpeningDate" type="date" value={formData.CashOpeningDate} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Balance</label>
                        <input name="StockOpeningBalance" type="number" value={formData.StockOpeningBalance} onChange={handleChange} placeholder="0.00" style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Date</label>
                        <input name="StockOpeningDate" type="date" value={formData.StockOpeningDate} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
            </div>

            {/* Footer Buttons */}
            <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                background: '#f8fafc',
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSaving}
                    style={{
                        borderColor: '#1e3a5f',
                        color: '#1e3a5f',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'none',
                        padding: '6px 20px',
                        borderRadius: '4px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSaving || !isFormValid}
                    style={{
                        background: '#1e3a5f',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'none',
                        padding: '6px 20px',
                        borderRadius: '4px',
                    }}
                >
                    {isSaving ? <CircularProgress size={14} color="inherit" style={{ marginRight: '6px' }} /> : null}
                    {isSaving ? 'Creating...' : 'Create Branch'}
                </Button>
            </div>
        </Dialog>
    );
};

export default BranchCreateModal;
